// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — bootstrap and the screen machine. Owns which screen is up, the room's
// Net (created ONCE per session, never left for a rematch), and the glue between
// the pure game/Race and the DOM. All rules live in game.ts; all map drawing in
// render.ts; all input in search.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf, guessableIds, type Mode } from './modes';
import { createGame, guessCountry, type GameState } from './game';
import { ALL, country, resolveTyped, type Country } from './data';
import { dailySeed, mysteryFor, todayUTC } from './seed';
import { createMapView, drawSilhouette, type MapView } from './render';
import { createSearch, type Search } from './search';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown, type Countdown } from './countdown';
import {
  aboutHtml,
  ATTRIB,
  guessRowHtml,
  helpHtml,
  menuHtml,
  multiResultsHtml,
  silhouetteBoxHtml,
  soloResultsHtml,
} from './ui';
import { heatColor } from './palette';

const SLUG = 'beeline';

// ── shell ───────────────────────────────────────────────────────────────────

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ─────────────────────────────────────────────────

let playerName = resolveName(store, () => `Explorer ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let streak = 0;
let best = store.get<number>('best', 0) ?? 0;

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ─────────────────────────────────────────────────────────

const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `
    <main class="main-content" id="content"></main>
    <footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

// ── menu ─────────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({
    modeId: myModeChoice,
    best,
    muted: sfx.muted(),
    dateStr: todayUTC(),
  });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = c.dataset.mode!;
      store.set('mode', myModeChoice);
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', showRoomEntry);
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── game screen (shared by solo + multiplayer) ───────────────────────────────

interface GameHooks {
  mode: Mode;
  getState: () => GameState;
  getMsLeft: () => number | null;
  getStreak: () => number | null;
  onGuess: (id: string) => void;
  onQuit: () => void;
}

interface GameScreen {
  update(): void;
  destroy(): void;
}

function buildGameScreen(hooks: GameHooks): GameScreen {
  const mode = hooks.mode;
  const mystery = country(hooks.getState().mysteryId)!;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-left" id="hudLeft"></span>
        <span class="hud-timer" id="hudTimer" hidden></span>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="mapwrap">
        <canvas class="map" id="map"></canvas>
        <div class="silbox" id="silbox"${mode.showSilhouette ? '' : ' hidden'}>
          ${silhouetteBoxHtml()}
        </div>
      </div>
      <div class="play">
        <div class="search-host" id="searchHost"></div>
        <ul class="guesslist" id="guesslist" aria-live="polite"></ul>
      </div>
    </section>`;

  const canvas = content.querySelector<HTMLCanvasElement>('#map')!;
  const mapView: MapView = createMapView(canvas);
  const guessList = content.querySelector<HTMLUListElement>('#guesslist')!;
  const hudLeft = content.querySelector<HTMLElement>('#hudLeft')!;
  const hudTimer = content.querySelector<HTMLElement>('#hudTimer')!;

  if (mode.showSilhouette) {
    // Defer one frame so the canvas has a measured size.
    requestAnimationFrame(() => drawSilhouette(content.querySelector<HTMLCanvasElement>('#sil')!, mystery));
    // The tile floats over the map; let the player collapse it out of the way
    // (reported: it covered the map on a phone) and bring it back on demand.
    const silbox = content.querySelector<HTMLElement>('#silbox')!;
    const silToggle = content.querySelector<HTMLButtonElement>('#silToggle')!;
    silToggle.addEventListener('click', () => {
      const collapsed = silbox.classList.toggle('collapsed');
      silToggle.textContent = collapsed ? 'Shape' : 'Hide';
      silToggle.setAttribute('aria-expanded', String(!collapsed));
      silToggle.setAttribute('aria-label', collapsed ? 'Show the country shape' : 'Hide the country shape');
    });
  }

  const poolSet = guessableIds(mode);
  const search: Search = createSearch({
    container: content.querySelector<HTMLElement>('#searchHost')!,
    pool: () => poolSet,
    taken: () => new Set(hooks.getState().guesses.map((g) => g.id)),
    onGuess: (id) => hooks.onGuess(id),
    placeholder: 'Guess a country…',
  });
  requestAnimationFrame(() => search.focus());

  content.querySelector('#quit')!.addEventListener('click', hooks.onQuit);

  function update(): void {
    const st = hooks.getState();
    guessList.innerHTML = st.guesses
      .slice()
      .reverse()
      .map(guessRowHtml)
      .join('');
    const left = mode.guesses - st.guesses.length;
    const streakNow = hooks.getStreak();
    hudLeft.textContent =
      streakNow !== null && streakNow > 0
        ? `${left} left · streak ${streakNow}`
        : `${left} guess${left === 1 ? '' : 'es'} left`;
    const ms = hooks.getMsLeft();
    if (ms !== null) {
      hudTimer.hidden = false;
      const s = Math.max(0, Math.ceil(ms / 1000));
      hudTimer.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      hudTimer.classList.toggle('low', s <= 15);
    }
    search.setDisabled(st.over);
    search.refresh();
    mapView.render({
      guesses: st.guesses,
      mysteryId: st.mysteryId,
      reveal: st.over,
      reducedMotion,
    });
  }

  update();

  return {
    update,
    destroy() {
      search.destroy();
      mapView.destroy();
    },
  };
}

/** Sound + haptic feedback for a guess. */
function guessFeedback(st: GameState): void {
  const g = st.guesses.at(-1);
  if (!g) return;
  try {
    if (g.correct) sfx.play('win');
    else sfx.play('coin', { pitch: 0.7 + (g.pct / 100) * 1.1 });
  } catch {
    /* audio best-effort */
  }
  if (!reducedMotion && 'vibrate' in navigator) {
    try {
      navigator.vibrate(g.correct ? [40, 30, 60] : 12);
    } catch {
      /* haptics best-effort */
    }
  }
}

// ── solo ─────────────────────────────────────────────────────────────────────

let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);

  soloScreen = buildGameScreen({
    mode,
    getState: () => soloState!,
    getMsLeft: () => null, // solo has no clock
    getStreak: () => (daily ? null : streak),
    onGuess: (id) => {
      const next = guessCountry(soloState!, id);
      if (next === soloState) return;
      soloState = next;
      guessFeedback(soloState);
      soloScreen!.update();
      if (soloState.over) {
        if (!daily && soloState.solved) {
          streak++;
          if (streak > best) {
            best = streak;
            store.set('best', best);
          }
        } else if (!daily) {
          streak = 0;
        }
        setTimeout(() => showSoloResults(), 850);
      }
    },
    onQuit: () => {
      if (!daily) streak = 0;
      showMenu();
    },
  });
  onCleanup(() => soloScreen?.destroy());

  runCountdown(document.body, sfx, () => {});
}

function showSoloResults(): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = modeOf(st.modeId);
  content.innerHTML = `
    ${soloResultsHtml(st, mode, streak, best, soloDaily)}
    <div class="menu-actions">
      <button class="btn primary" id="again">${soloDaily ? 'Play random' : st.solved ? 'Next country' : 'Try another'}</button>
      <button class="btn" id="share">Share result</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const squares = st.guesses
    .map((g) =>
      g.correct
        ? '🟩'
        : g.pct >= 75
          ? '🟥'
          : g.pct >= 55
            ? '🟧'
            : g.pct >= 35
              ? '🟨'
              : g.pct >= 18
                ? '🟦'
                : '⬜',
    )
    .join('');
  const head = soloDaily ? `Beeline ${todayUTC()} · ${mode.name}` : `Beeline · ${mode.name}`;
  const line = st.solved ? `solved in ${st.guesses.length}` : 'not solved';
  const text = `${head} — ${line}\n${squares}\nhttps://beeline.benrichardson.dev`;
  return share(text);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Result copied');
  } catch {
    toast('Could not copy');
  }
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── multiplayer ──────────────────────────────────────────────────────────────

let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;
let loopTimer: ReturnType<typeof setInterval> | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code, created) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone races the same hidden country. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
      onPeerJoin: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerReturned(seat);
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${modeOf(id).name}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${modeOf(hostOptsMode).name}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = b.dataset.mode!;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        // Wins are tallied once, at round close, in refreshMultiResults().
        rounds?.finish();
        setTimeout(() => showMultiResults(), 700);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    // Spectator: no board, straight to the live results view.
    showMultiResults();
  } else {
    gameScreen = buildGameScreen({
      mode,
      getState: () => rc.state,
      getMsLeft: () => rc.msLeft,
      getStreak: () => null,
      onGuess: (id) => {
        if (rc.guess(id)) {
          guessFeedback(rc.state);
          gameScreen?.update();
        }
      },
      onQuit: () => backToLobby(),
    });
    onCleanup(() => {
      gameScreen?.destroy();
      gameScreen = null;
    });
    const cd: Countdown = runCountdown(document.body, sfx, () => {});
    onCleanup(() => cd.cancel());
  }

  // The authoritative-free clock: every seated peer ticks its OWN timer on an
  // interval (never rAF alone), so the round always ends and the results resolve.
  loopTimer = setInterval(() => {
    rc.tick(250);
    gameScreen?.update();
  }, 250);
  onCleanup(() => {
    if (loopTimer) clearInterval(loopTimer);
    loopTimer = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────

let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(20_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  // Award the round's win(s) exactly once, when everyone is done.
  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const winner = rc.ranked().find((r) => r.solved && !r.left);
    if (winner) tally[winner.seat] = (tally[winner.seat] ?? 0) + 1;
  }

  box.innerHTML = multiResultsHtml(
    rc.ranked(),
    rc.selfSeat,
    rc.state.mysteryId,
    tally,
  );

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.over).map((x) => x.name);
      waiting.textContent = left.length ? `Still guessing: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ─────────────────────────────────────────────────────────────────────

function boot(): void {
  hardenViewport();
  mountShell();
  const room = new URL(location.href).searchParams.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// A hook the browser-verification pass drives to make guesses without the search
// UI (synthetic pointer events do not reliably reach a canvas). Test-only.
(window as unknown as { __beeline?: unknown }).__beeline = {
  guessSolo: (nameOrId: string): GameState | null => {
    if (!soloState || !soloScreen) return null;
    const c = resolveTyped(nameOrId, new Set(ALL.map((k) => k.id))) ?? country(nameOrId);
    if (!c) return null;
    const next = guessCountry(soloState, c.id);
    if (next !== soloState) {
      soloState = next;
      guessFeedback(next);
      soloScreen.update();
    }
    return soloState;
  },
  state: (): GameState | null => soloState,
  mysteryName: (): string | null =>
    soloState ? (country(soloState.mysteryId)?.name ?? null) : null,
  mysteryFor: (modeId: string, seed: number): Country => mysteryFor(modeOf(modeId), seed),
  heat: heatColor,
};

boot();
